import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Card, Button } from './ui';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';
import styles from './StudentForm.module.css';

// Mirrors the backend: only these roles may place a student in a house
const HOUSE_ROLES = ['administrator', 'principal', 'head_of_academics'];

const EMPTY_STUDENT = {
  first_names: '', surname: '', national_id: '', class_id: '', phone: '', address: '', gender: '', house_id: '', date_of_birth: '',
};
const EMPTY_GUARDIAN = { full_name: '', relationship: '', phone: '', email: '', address: '' };
const EMPTY_MEDICAL = { blood_group: '', allergies: '', medical_conditions: '', medications: '', medical_notes: '' };
// Used until GET /students/form-options answers (backend/student_details.py is the source)
const DEFAULT_OPTIONS = { relationships: ['Mother', 'Father', 'Guardian', 'Other'], blood_groups: [], max_guardians: 4 };

const isBlank = (guardian) => Object.values(guardian).every((value) => !String(value || '').trim());

function StudentForm({ token, user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [formData, setFormData] = useState(EMPTY_STUDENT);
  const [guardians, setGuardians] = useState([{ ...EMPTY_GUARDIAN }]);
  const [medical, setMedical] = useState(EMPTY_MEDICAL);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(isEdit);
  const [message, setMessage] = useState('');
  const [classes, setClasses] = useState([]);
  const [houses, setHouses] = useState([]);
  const [needsReEnrollment, setNeedsReEnrollment] = useState(false);
  const canSetHouse = user?.features?.houses !== false && hasAnyRole(user, HOUSE_ROLES);

  useEffect(() => {
    if (!canSetHouse) return;
    fetch('/api/v1/houses', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : []))
      .then(setHouses)
      .catch(() => {});
  }, [token, canSetHouse]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    // Neither is essential to the page loading -- the selects just stay short.
    fetch('/api/v1/classes', { headers })
      .then((response) => (response.ok ? response.json() : []))
      .then(setClasses)
      .catch(() => {});
    fetch('/api/v1/students/form-options', { headers })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (data) setOptions(data); })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const response = await fetch(`/api/v1/students/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setFormData({
            first_names: data.first_names || data.full_name || '', surname: data.surname || '',
            national_id: data.national_id || '', class_id: data.class_id || '', phone: data.phone || '',
            address: data.address || '', gender: data.gender || '',
            house_id: data.house_id || '', date_of_birth: data.date_of_birth || '',
          });
          const saved = (data.guardians || []).map((guardian) => ({
            full_name: guardian.full_name || '', relationship: guardian.relationship || '',
            phone: guardian.phone || '', email: guardian.email || '', address: guardian.address || '',
          }));
          setGuardians(saved.length ? saved : [{ ...EMPTY_GUARDIAN }]);
          setMedical({
            blood_group: data.blood_group || '', allergies: data.allergies || '',
            medical_conditions: data.medical_conditions || '', medications: data.medications || '',
            medical_notes: data.medical_notes || '',
          });
          setNeedsReEnrollment(Boolean(data.class_name) && !data.class_id);
        } else {
          setMessage({ type: 'error', text: 'Failed to load student' });
        }
      } catch {
        setMessage({ type: 'error', text: 'Network error' });
      } finally {
        setLoadingStudent(false);
      }
    })();
  }, [id, isEdit, token]);

  const validateNationalId = (nid) => /^\d{2}-\d{6,7}[A-Z]\d{2}$/.test(nid);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleMedicalChange = (e) => setMedical({ ...medical, [e.target.name]: e.target.value });
  const updateGuardian = (index, field, value) =>
    setGuardians(guardians.map((guardian, i) => (i === index ? { ...guardian, [field]: value } : guardian)));
  const addGuardian = () => setGuardians([...guardians, { ...EMPTY_GUARDIAN }]);
  const removeGuardian = (index) => {
    const remaining = guardians.filter((_, i) => i !== index);
    setGuardians(remaining.length ? remaining : [{ ...EMPTY_GUARDIAN }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.national_id && !validateNationalId(formData.national_id)) {
      setMessage({ type: 'error', text: 'Invalid National ID format. Expected: 00-000000A00' });
      return;
    }
    const filledGuardians = guardians.filter((guardian) => !isBlank(guardian));
    const unnamed = filledGuardians.findIndex((guardian) => !guardian.full_name.trim());
    if (unnamed !== -1) {
      setMessage({ type: 'error', text: `Guardian ${unnamed + 1}: please enter their name, or clear the other fields.` });
      return;
    }
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(isEdit ? `/api/v1/students/${id}` : '/api/v1/students', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...formData, ...medical, guardians: filledGuardians }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: isEdit ? 'Student updated successfully!' : 'Student created successfully!' });
        setTimeout(() => navigate('/students'), 1500);
      } else {
        setMessage({ type: 'error', text: await safeErrorMessage(response, `Failed to ${isEdit ? 'update' : 'create'} student`) });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingStudent) return <Card>Loading student...</Card>;

  return (
    <form onSubmit={handleSubmit}>
      <h2 className={styles.pageTitle}>{isEdit ? 'Edit Student' : 'Add New Student'}</h2>

      {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>Student details</h3>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="student-first-names">First names *</label>
            <input id="student-first-names" type="text" name="first_names" value={formData.first_names} onChange={handleChange} maxLength={60} placeholder="e.g., Tendai Blessing" required />
          </div>
          <div className="form-group">
            <label htmlFor="student-surname">Surname *</label>
            <input id="student-surname" type="text" name="surname" value={formData.surname} onChange={handleChange} maxLength={40} placeholder="e.g., Moyo" required />
          </div>
        </div>
        <div className={styles.threeColumns}>
          <div className="form-group">
            <label htmlFor="student-gender">Gender</label>
            <select id="student-gender" name="gender" value={formData.gender} onChange={handleChange}>
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="student-dob">Date of Birth</label>
            <input id="student-dob" type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} max={new Date().toISOString().slice(0, 10)} />
          </div>
          <div className="form-group">
            <label htmlFor="student-national-id">National ID</label>
            <input id="student-national-id" type="text" name="national_id" value={formData.national_id} onChange={handleChange} placeholder="50-1234567A90" disabled={isEdit} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="student-class">Class</label>
            <select id="student-class" name="class_id" value={formData.class_id} onChange={handleChange}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
            </select>
            {needsReEnrollment && (
              <small style={{ color: 'var(--color-warning, #b45309)' }}>
                This student has a legacy class value that needs re-enrollment — please select a class above.
              </small>
            )}
          </div>
          {canSetHouse && (
            <div className="form-group">
              <label htmlFor="student-house">House</label>
              <select id="student-house" name="house_id" value={formData.house_id} onChange={handleChange}>
                <option value="">No house</option>
                {houses.map((house) => <option key={house.id} value={house.id}>{house.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="student-phone">Student's phone</label>
            <input id="student-phone" type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="+263712345678" maxLength={60} />
            <small>Separate several numbers with "/".</small>
          </div>
          <div className="form-group">
            <label htmlFor="student-address">Home address</label>
            <textarea id="student-address" name="address" value={formData.address} onChange={handleChange} rows="2" />
          </div>
        </div>
      </Card>

      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <div>
            <h3 className={styles.sectionTitle}>Parents / guardians</h3>
            <p className={styles.sectionHint}>
              The first one is the main contact: fee reminders and the parent login invitation go to them.
            </p>
          </div>
        </div>
        {guardians.map((guardian, index) => (
          <div key={index} className={styles.guardian} role="group" aria-label={`Guardian ${index + 1}`}>
            <div className={styles.guardianHead}>
              <h4 className={styles.guardianTitle}>
                Guardian {index + 1} {index === 0 && <span className={styles.guardianNote}>— main contact</span>}
              </h4>
              {(guardians.length > 1 || !isBlank(guardian)) && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeGuardian(index)} aria-label={`Remove guardian ${index + 1}`}>
                  <TrashIcon style={{ width: 14, height: 14 }} />
                  Remove
                </Button>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`guardian-name-${index}`}>Full name</label>
                <input id={`guardian-name-${index}`} type="text" value={guardian.full_name} onChange={(e) => updateGuardian(index, 'full_name', e.target.value)} maxLength={100} />
              </div>
              <div className="form-group">
                <label htmlFor={`guardian-relationship-${index}`}>Relationship to student</label>
                <select id={`guardian-relationship-${index}`} value={guardian.relationship} onChange={(e) => updateGuardian(index, 'relationship', e.target.value)}>
                  <option value="">Select relationship</option>
                  {options.relationships.map((relationship) => <option key={relationship} value={relationship}>{relationship}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={`guardian-phone-${index}`}>Phone</label>
                <input id={`guardian-phone-${index}`} type="text" value={guardian.phone} onChange={(e) => updateGuardian(index, 'phone', e.target.value)} placeholder="0712345678 / 0772345678" maxLength={60} />
                <small>Separate several numbers with "/".</small>
              </div>
              <div className="form-group">
                <label htmlFor={`guardian-email-${index}`}>Email</label>
                <input id={`guardian-email-${index}`} type="email" value={guardian.email} onChange={(e) => updateGuardian(index, 'email', e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor={`guardian-address-${index}`}>Address, if different from the student's</label>
              <input id={`guardian-address-${index}`} type="text" value={guardian.address} onChange={(e) => updateGuardian(index, 'address', e.target.value)} />
            </div>
          </div>
        ))}
        {guardians.length < options.max_guardians && (
          <Button type="button" variant="secondary" size="sm" onClick={addGuardian}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add another parent / guardian
          </Button>
        )}
      </Card>

      <Card className={styles.section}>
        <div className={styles.sectionHead}>
          <div>
            <h3 className={styles.sectionTitle}>Medical information</h3>
            <p className={styles.sectionHint}>Seen by school staff who work with this student and by the student's own family.</p>
          </div>
          <span className={styles.optional}>Optional</span>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="student-blood-group">Blood group</label>
            <select id="student-blood-group" name="blood_group" value={medical.blood_group} onChange={handleMedicalChange}>
              <option value="">Not known</option>
              {options.blood_groups.map((group) => <option key={group} value={group}>{group}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="student-allergies">Allergies</label>
            <input id="student-allergies" type="text" name="allergies" value={medical.allergies} onChange={handleMedicalChange} placeholder="e.g., Peanuts, penicillin" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="student-conditions">Medical conditions</label>
            <textarea id="student-conditions" name="medical_conditions" value={medical.medical_conditions} onChange={handleMedicalChange} rows="2" placeholder="e.g., Asthma" />
          </div>
          <div className="form-group">
            <label htmlFor="student-medications">Regular medication</label>
            <textarea id="student-medications" name="medications" value={medical.medications} onChange={handleMedicalChange} rows="2" placeholder="e.g., Inhaler, as needed" />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="student-medical-notes">Other notes</label>
          <textarea id="student-medical-notes" name="medical_notes" value={medical.medical_notes} onChange={handleMedicalChange} rows="2" placeholder="Doctor, medical aid, or anything staff should know in an emergency" />
        </div>
      </Card>

      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Student')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate('/students')}>Cancel</Button>
      </div>
    </form>
  );
}

export default StudentForm;
